import { TabView } from 'react-native-tab-view';
import { useCallback, useMemo, useRef } from 'react';
import { useScreenSize } from '@banhala/ui-kit';
import { isWeb } from 'shared/src/utils';
import type { LikeTabItem } from '../types';
import { useLogTabNavigation } from '../hooks/useLogTabNavigation';
import { LikeFolder } from './LikeFolder';
import { LikeGoods } from './LikeGoods';
import { LikeMarket } from './LikeMarket';
import { LikeTabBar } from './LikeTabBar';
import { LikeWebView } from './LikeWebView';

export type LikeTabRoute = {
  key: string;
} & LikeTabItem;

interface LikeContentsProps {
  tabs: LikeTabItem[];
  index: number;
  onIndexChange: (index: number) => void;
}

const renderCustomContent = (id: string, isActive: boolean) => {
  switch (id) {
    case 'goods':
      return <LikeGoods isActive={isActive} />;
    case 'folder':
      return <LikeFolder isActive={isActive} />;
    case 'market':
      return <LikeMarket isActive={isActive} />;
    default:
      return null;
  }
};

const renderWebViewContent = (defaultQuery: Record<string, unknown> | null) => {
  const url = defaultQuery?.url;
  if (typeof url !== 'string') return null;
  return <LikeWebView url={url} />;
};

const renderContent = (route: LikeTabRoute, isActive: boolean) => {
  switch (route.type) {
    case 'CUSTOM':
      return renderCustomContent(route.id, isActive);
    case 'WEBVIEW':
      return renderWebViewContent(route.default_query);
  }
};

export const LikeContents = ({
  tabs,
  index,
  onIndexChange,
}: LikeContentsProps) => {
  const { width } = useScreenSize();

  const routes = useMemo<LikeTabRoute[]>(
    () =>
      tabs.map(item => ({
        ...item,
        key: item.id,
      })),
    [tabs],
  );

  const { logClickUpperTab } = useLogTabNavigation(routes);

  const indexRef = useRef(index);
  indexRef.current = index;

  const handleTabClick = useCallback(
    (nextIndex: number) => {
      logClickUpperTab(indexRef.current, nextIndex);
    },
    [logClickUpperTab],
  );

  if (routes.length === 0) return null;

  return (
    <TabView<LikeTabRoute>
      lazy={true}
      navigationState={{ index, routes }}
      renderScene={({ route }) =>
        renderContent(route, routes[index]?.id === route.id)
      }
      onIndexChange={onIndexChange}
      initialLayout={{ width }}
      swipeEnabled={!isWeb}
      renderTabBar={props => (
        <LikeTabBar {...props} onTabClick={handleTabClick} />
      )}
    />
  );
};
